<script setup>
import { computed, onMounted, ref } from 'vue'
import { GitBranch, MessageSquareText, Search } from '@lucide/vue'
import {
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Pagination,
  Section,
  Select,
  Spinner,
  Toolbar,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { listLlmConversations } from '../../api/llm/index.js'
import { formatDateTime, formatOptionalInt } from './llm-format.js'
import ConversationDetailDrawer from './conversation/ConversationDetailDrawer.vue'

const WINDOWS = {
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
}
const PAGE_SIZES = [20, 50, 100]

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const filters = ref(defaultFilters())
const conversations = ref([])
const total = ref(0)
const pages = ref(0)
const page = ref(1)
const pageSize = ref(PAGE_SIZES[0])
const loading = ref(false)
const error = ref('')
const drawerOpen = ref(false)
const selectedRunId = ref('')
const selectedConversationKey = ref('')
const selectedTurnId = ref('')

const windowOptions = computed(() => Object.keys(WINDOWS).map((value) => ({
  value,
  label: t(`visualGameAdmin.llm.conversations.filters.windows.${value}`),
})))

const roleOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.llm.conversations.filters.allRoles') },
  ...['kp', 'npc', 'npc_action_kp'].map((value) => ({
    value,
    label: roleLabel(value),
  })),
])
const sortOptions = computed(() => ['last_activity_at', 'call_count', 'total_tokens'].map((value) => ({
  value,
  label: t(`visualGameAdmin.llm.conversations.filters.sorts.${value}`),
})))
const orderOptions = computed(() => ['desc', 'asc'].map((value) => ({
  value,
  label: t(`visualGameAdmin.llm.conversations.filters.orders.${value}`),
})))
const pageCount = computed(() => Math.max(1, pages.value))
const pageSummary = computed(() => t('visualGameAdmin.llm.conversations.pagination.summary', {
  total: total.value.toLocaleString(),
  page: page.value,
  pages: pageCount.value,
}))

onMounted(async () => {
  if (typeof route.query.q === 'string') filters.value.q = route.query.q
  await load()
  if (
    typeof route.query.run_id === 'string'
    && typeof route.query.conversation_key === 'string'
  ) {
    selectedRunId.value = route.query.run_id
    selectedConversationKey.value = route.query.conversation_key
    selectedTurnId.value = typeof route.query.turn_id === 'string' ? route.query.turn_id : ''
    drawerOpen.value = true
  }
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const query = {
      since: new Date(Date.now() - WINDOWS[filters.value.window]).toISOString(),
      page: page.value,
      limit: pageSize.value,
      sort: filters.value.sort,
      order: filters.value.order,
    }
    const q = filters.value.q.trim()
    if (q !== '') query.q = q
    if (filters.value.role !== '') query.agent_role = filters.value.role
    const response = await listLlmConversations(query)
    conversations.value = response.items
    total.value = response.total
    pages.value = response.pages
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

function search() {
  page.value = 1
  return load()
}

function reset() {
  filters.value = defaultFilters()
  page.value = 1
  return load()
}

function changePageSize(value) {
  pageSize.value = value
  page.value = 1
  return load()
}

function nextPage() {
  page.value += 1
  return load()
}

function previousPage() {
  page.value -= 1
  return load()
}

function defaultFilters() {
  return {
    window: 'week',
    q: '',
    role: '',
    sort: 'last_activity_at',
    order: 'desc',
  }
}

function openConversation(conversation) {
  selectedRunId.value = conversation.run_id
  selectedConversationKey.value = conversation.conversation_key
  selectedTurnId.value = ''
  drawerOpen.value = true
  router.replace({
    query: {
      ...route.query,
      run_id: conversation.run_id,
      conversation_key: conversation.conversation_key,
      turn_id: undefined,
    },
  })
}

function openRunVideoMatch(conversation) {
  router.push({ name: 'llm-run-video-match', params: { runId: conversation.run_id } })
}

function roleLabel(role) {
  switch (role) {
    case 'kp':
    case 'npc':
    case 'npc_action_kp':
      return t(`visualGameAdmin.llm.conversations.roles.${role}`)
    default:
      throw new Error(`Unhandled conversation agent role: ${role}`)
  }
}

function roleVariant(role) {
  switch (role) {
    case 'kp':
      return 'info'
    case 'npc':
      return 'success'
    case 'npc_action_kp':
      return 'warning'
    default:
      throw new Error(`Unhandled conversation agent role: ${role}`)
  }
}

function identity(conversation) {
  if (conversation.user_id !== null) {
    return t('visualGameAdmin.llm.conversations.list.identityUser', { id: conversation.user_id })
  }
  if (conversation.device_id === null) {
    throw new Error(`Conversation has neither user nor device: run ${conversation.run_id}`)
  }
  return t('visualGameAdmin.llm.conversations.list.identityGuest', { id: conversation.device_id })
}

function tokenSummary(conversation) {
  return t('visualGameAdmin.llm.conversations.list.tokens', {
    input: formatOptionalInt(conversation.prompt_tokens),
    output: formatOptionalInt(conversation.completion_tokens),
    cached: formatOptionalInt(conversation.cached_tokens),
    cacheWrite: formatOptionalInt(conversation.cache_write_tokens),
  })
}
</script>

<template>
  <section class="conversations-page vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.conversations.title')" />

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.conversations.filters.window') }}</label>
          <Select v-model="filters.window" :options="windowOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.llm.conversations.filters.keyword') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="t('visualGameAdmin.llm.conversations.filters.keywordPlaceholder')"
            @keyup.enter="search"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.conversations.filters.role') }}</label>
          <Select v-model="filters.role" :options="roleOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.conversations.filters.sort') }}</label>
          <Select v-model="filters.sort" :options="sortOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.conversations.filters.order') }}</label>
          <Select v-model="filters.order" :options="orderOptions" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="search">
            <Search :size="14" />
            {{ t('visualGameAdmin.llm.conversations.filters.search') }}
          </Button>
          <Button size="sm" variant="secondary" :disabled="loading" @click="reset">
            {{ t('visualGameAdmin.llm.conversations.filters.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading && conversations.length === 0" class="vga-state">
      <Spinner size="lg" :label="t('visualGameAdmin.common.loading')" />
    </div>

    <div v-else-if="error" class="vga-state">
      <p class="vga-error" role="alert">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>

    <EmptyState
      v-else-if="conversations.length === 0"
      :title="t('visualGameAdmin.llm.conversations.empty.title')"
      :description="t('visualGameAdmin.llm.conversations.empty.description')"
    />

    <ol v-else class="conversation-list">
      <li v-for="conversation in conversations" :key="`${conversation.run_id}:${conversation.conversation_key}`">
        <article class="conversation-card">
          <div class="conversation-card__icon"><MessageSquareText :size="22" /></div>
          <div class="conversation-card__main">
            <header>
              <div>
                <strong>{{ conversation.scenario_title }}</strong>
                <span class="vga-mono">{{ conversation.run_id }} · {{ conversation.conversation_key }}</span>
              </div>
              <Badge :variant="roleVariant(conversation.agent_role)">{{ roleLabel(conversation.agent_role) }}</Badge>
            </header>
            <div class="conversation-card__meta">
              <span>{{ identity(conversation) }}</span>
              <span>{{ t('visualGameAdmin.llm.conversations.list.calls', {
                count: conversation.call_count,
              }) }}</span>
              <span>{{ tokenSummary(conversation) }}</span>
              <span>{{ t('visualGameAdmin.llm.conversations.list.lastActivity', { time: formatDateTime(conversation.last_activity_at) }) }}</span>
            </div>
          </div>
          <div class="conversation-card__actions">
            <Button variant="secondary" @click="openConversation(conversation)">
              {{ t('visualGameAdmin.llm.conversations.list.open') }}
            </Button>
            <Button variant="ghost" @click="openRunVideoMatch(conversation)">
              <GitBranch :size="16" />
              {{ t('visualGameAdmin.llm.runVideoMatch.openRun') }}
            </Button>
          </div>
        </article>
      </li>
    </ol>

    <Pagination
      v-if="!loading && !error && conversations.length > 0"
      :has-next="page < pageCount"
      :has-prev="page > 1"
      :loading="loading"
      :prev-label="t('visualGameAdmin.llm.conversations.pagination.previous')"
      :next-label="t('visualGameAdmin.llm.conversations.pagination.next')"
      :summary="pageSummary"
      :page-size="pageSize"
      :page-size-options="PAGE_SIZES"
      :page-size-label="t('visualGameAdmin.llm.conversations.pagination.pageSize')"
      @prev="previousPage"
      @next="nextPage"
      @update:page-size="changePageSize"
    />

    <ConversationDetailDrawer
      v-model:open="drawerOpen"
      :run-id="selectedRunId"
      :conversation-key="selectedConversationKey"
      :turn-id="selectedTurnId"
    />
  </section>
</template>

<style scoped>
.conversation-list {
  display: grid;
  gap: var(--drama-space-md);
  margin: 0;
  padding: 0;
  list-style: none;
}

.conversation-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--drama-space-lg);
  padding: var(--drama-space-lg);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface);
}

.conversation-card:hover {
  border-color: var(--drama-color-border-strong);
}

.conversation-card__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-primary);
}

.conversation-card__main,
.conversation-card__main header > div {
  min-width: 0;
}

.conversation-card__main {
  display: grid;
  gap: var(--drama-space-md);
}

.conversation-card__main header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.conversation-card__main header > div {
  display: grid;
  gap: var(--drama-space-xs);
}

.conversation-card__main header span {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  overflow-wrap: anywhere;
}

.conversation-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm) var(--drama-space-lg);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.conversation-card__actions {
  display: grid;
  gap: var(--drama-space-xs);
  justify-items: stretch;
}

@media (max-width: 880px) {
  .conversation-card {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .conversation-card__actions {
    grid-column: 2;
    justify-self: start;
  }
}

@media (max-width: 560px) {
  .conversation-card {
    grid-template-columns: 1fr;
  }

  .conversation-card__icon {
    display: none;
  }

  .conversation-card__actions {
    grid-column: 1;
  }
}
</style>
